import { useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { getFilterStorageKey, getUrlOrSessionStorageValue } from '@common/constants/StoragePrefix';

interface UseFiltersOptions {
	filterKeys?: string[];
	arrayFilterKeys?: string[];
}

/**
 * Custom hook for retrieving filters from URL parameters or session storage.
 *
 * @param {Object} options - Configuration options for filters.
 * @param {string[]} [options.filterKeys=[]] - Keys for string-based filters.
 * @param {string[]} [options.arrayFilterKeys=[]] - Keys for array-based filters.
 * @returns {Record<string, string | string[]>} - An object containing
 * all filters (string and array filters combined).
 */
function useFilters({
	filterKeys = [],
	arrayFilterKeys = [],
}: UseFiltersOptions): Record<string, string | string[]> {
	const [searchParams] = useSearchParams();
	const location = useLocation();

	const filters = useMemo(() => {
		const stringFilters = filterKeys.reduce<Record<string, string | null>>((acc, key) => {
			const value = getUrlOrSessionStorageValue(
				searchParams,
				location.pathname,
				key,
				getFilterStorageKey,
			);
			if (value) acc[key] = value;
			return acc;
		}, {});

		const arrayFilters = arrayFilterKeys.reduce<Record<string, string[]>>((acc, key) => {
			const value = getUrlOrSessionStorageValue(
				searchParams,
				location.pathname,
				key,
				getFilterStorageKey,
			);
			if (value) acc[key] = value.split(',');
			return acc;
		}, {});

		return { ...stringFilters, ...arrayFilters };
	}, [filterKeys, arrayFilterKeys, searchParams, location.pathname]);

	return filters;
}

export default useFilters;
